import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Info, Star, ArrowRight } from 'lucide-react';
import { Product } from '../types';
import PriceWithVatLabel from './PriceWithVatLabel';
import ProductNewRibbon from './ProductNewRibbon';
import { trackBookingCtaClick, trackProductLinkClick } from '../utils/siteAnalytics';

interface ProductCardProps {
  product: Product;
  listName?: string;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, listName = 'Produkter' }) => {
  const handleProductNavigate = () => {
    trackProductLinkClick(product, listName);
  };

  return (
    <Link
      to={`/produkter/${product.slug || product.id}`}
      onClick={handleProductNavigate}
      className="group bg-slate-900/35 border border-slate-800 overflow-hidden transition-colors hover:border-slate-700 flex flex-col h-full"
    >
      <div className="relative h-44 md:h-64 border-b border-slate-800">
        <div className="absolute inset-0 bg-slate-900/20 overflow-hidden">
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-contain group-hover:scale-[1.05] transition-transform duration-300"
            loading="lazy"
          />
        </div>
        <ProductNewRibbon newUntil={product.newUntil} size="md" />
        {product.popular && (
          <div className="absolute top-4 right-4 bg-purple-600 text-white text-xs font-medium px-3 py-1 rounded-none z-10">
            Populär
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
      </div>

      <div className="p-4 md:p-6 flex flex-col flex-grow">
        <div className="mb-2 md:mb-3">
          <span className="inline-flex items-center bg-slate-800 border border-slate-700 text-slate-200 text-xs font-medium px-3 py-1 rounded-none">
            {product.category}
          </span>
        </div>

        <h3 className="text-lg md:text-xl font-bold text-white mb-2 md:mb-3 group-hover:text-purple-300 transition-colors line-clamp-2">
          {product.name}
        </h3>
        <p className="text-slate-400 mb-4 md:mb-6 line-clamp-2 md:line-clamp-3 leading-relaxed flex-grow">
          {product.shortDescription}
        </p>

        <div className="flex justify-between items-center mb-4 md:mb-6 mt-auto">
          <div>
            {product.priceOnRequest ? (
              <>
                <div className="text-lg md:text-xl font-bold text-purple-300">Enligt offert</div>
                <div className="text-sm text-slate-400">Begär offert</div>
              </>
            ) : (
              <>
                <PriceWithVatLabel
                  price={product.pricePerDay}
                  valueClassName="text-xl md:text-2xl font-bold text-white"
                  labelClassName="text-xs text-slate-400"
                />
                <div className="text-sm text-slate-400">per dag</div>
              </>
            )}
          </div>
          <div className="flex items-center sr-only">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
            ))}
            <span className="text-sm text-slate-500 ml-1">(5.0)</span>
          </div>
        </div>

        <div className="flex flex-col space-y-2">
          {product.priceOnRequest ? (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                window.location.href = `/produkter/${product.slug || product.id}`;
              }}
              className="w-full inline-flex justify-center items-center bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 px-4 rounded-none transition-colors group/btn"
            >
              <Info size={18} className="mr-2" />
              Begär offert
              <ArrowRight size={16} className="ml-2 group-hover/btn:translate-x-1 transition-transform" />
            </button>
          ) : (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                trackBookingCtaClick(product, undefined, {
                  entry_point: 'list',
                  list_name: listName,
                });
                window.location.href = `/booking/${product.id}`;
              }}
              className="w-full inline-flex justify-center items-center bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 px-4 rounded-none transition-colors group/btn"
            >
              <Calendar size={18} className="mr-2" />
              Boka nu
              <ArrowRight size={16} className="ml-2 group-hover/btn:translate-x-1 transition-transform" />
            </button>
          )}
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
